import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setDischargeField,
  saveDischargeSummaryAction,
} from "../../../../Redux/actions/dischargeActions";
import { FiEye, FiPrinter, FiCheckCircle } from "react-icons/fi";

export default function DoctorAuthentication({ onSaveSuccess }) {
  const dispatch = useDispatch();
  const dischargeState = useSelector((state) => state.discharge);
  const {
    patientCounseled = "Y",
    digitalSignature = "base 64 image",
    isSaving,
    dischSummaryId,
  } = dischargeState || {};

  // Read doctor info from session storage
  const loginData = sessionStorage.getItem("login_result");
  const loginDataObject = loginData ? JSON.parse(loginData) : null;
  const sessionDoctorName = loginDataObject?.user?.userName || loginDataObject?.user?.firstName
    ? `Dr. ${loginDataObject?.user?.firstName || ""} ${loginDataObject?.user?.lastName || ""}`.trim()
    : "Doctor";
  const sessionDoctorId = loginDataObject?.user?.userId || "DOC001";

  const handleSaveDischarge = () => {
    // Construct clean payload matching the exact backend structure
    const nowStr = new Date().toISOString().replace("T", " ").substring(0, 19);

    const payload = {
      clinicalSummary: dischargeState.clinicalSummary || "",

      dischargeDiagnosis: (dischargeState.dischargeDiagnosis || []).map(
        (diag, idx) => ({
          activeFlag: diag.activeFlag || "Y",
          createdBy: diag.createdBy || "ADMIN",
          createdDate: diag.createdDate || nowStr,
          diagnosisCode: diag.diagnosisCode || "R50.9",
          diagnosisDesc: diag.diagnosisDesc || "Acute Fever",
          diagnosisSeq: idx + 1,
          diagnosisStatus: diag.diagnosisStatus || "CONFIRMED",
          diagnosisSystem: diag.diagnosisSystem || "ICD10",
          diagnosisType: diag.diagnosisType || (idx === 0 ? "PRIMARY" : "SECONDARY"),
          dischSummaryId: dischargeState.dischSummaryId || null,
          dischargeDiagnosisId: diag.dischargeDiagnosisId || null,
          encounterId: dischargeState.encounterId || "ENC001",
          modifiedBy: "ADMIN",
          modifiedDate: nowStr,
          patientId: dischargeState.patientId || "PAT001",
          presentOnAdmission: diag.presentOnAdmission || "Y",
          remarks: diag.remarks || "",
          status: 1,
        })
      ),

      chiefComplaint: dischargeState.chiefComplaint || "",

      dischargeAllergy: (dischargeState.dischargeAllergy || []).map((alg) => ({
        allergyId: alg.allergyId || "ALG001",
        allergyName: alg.allergyName || "",
        comments: alg.comments || "",
        createdBy: "ADMIN",
        createdDate: nowStr,
        dischSummaryId: dischargeState.dischSummaryId || null,
        dischargeAllergyId: alg.dischargeAllergyId || null,
        modifiedBy: "ADMIN",
        modifiedDate: nowStr,
        onsetDate: alg.onsetDate || nowStr,
        reaction: alg.reaction || "",
        severity: alg.severity || "Moderate",
      })),

      dischargeTreatmentSummary: {
        comments: dischargeState.dischargeTreatmentSummary?.comments || "",
        createdBy: "ADMIN",
        createdDate: nowStr,
        dietPlanDesc:
          dischargeState.dischargeTreatmentSummary?.dietPlanDesc || "Soft Diet",
        dietPlanId:
          dischargeState.dischargeTreatmentSummary?.dietPlanId || "DIET001",
        dischSummaryId: dischargeState.dischSummaryId || null,
        ivFluid:
          dischargeState.dischargeTreatmentSummary?.ivFluid || "IVF001",
        ivFluidDesc:
          dischargeState.dischargeTreatmentSummary?.ivFluidDesc || "Normal Saline",
        modifiedBy: "ADMIN",
        modifiedDate: nowStr,
        oxygenTherapyDesc:
          dischargeState.dischargeTreatmentSummary?.oxygenTherapyDesc || "Not Required",
        oxygenTherapyId:
          dischargeState.dischargeTreatmentSummary?.oxygenTherapyId || "OXY001",
        physiotherapyDesc:
          dischargeState.dischargeTreatmentSummary?.physiotherapyDesc || "Not Required",
        physiotherapyId:
          dischargeState.dischargeTreatmentSummary?.physiotherapyId || "PHY001",
        procedureDesc:
          dischargeState.dischargeTreatmentSummary?.procedureDesc || "Routine Medical Management",
        procedureId:
          dischargeState.dischargeTreatmentSummary?.procedureId || "PROC001",
        status: 1,
        surgeryDetails:
          dischargeState.dischargeTreatmentSummary?.surgeryDetails || "No surgery performed.",
        treatmentId:
          dischargeState.dischargeTreatmentSummary?.treatmentId || null,
      },

      conditionDesc: dischargeState.conditionDesc || "Stable",
      conditionId: dischargeState.conditionId || "COND001",
      conditionRemarks: dischargeState.conditionRemarks || "",

      dischargeMedication: (dischargeState.dischargeMedication || []).map(
        (med, idx) => ({
          activeYn: med.activeYn || "Y",
          createdBy: "ADMIN",
          createdDate: nowStr,
          dischSummaryId: dischargeState.dischSummaryId || null,
          displayOrder: idx + 1,
          dosage: med.dosage || "1 Tablet",
          drugId: med.drugId || `DRG00${idx + 1}`,
          drugName: med.drugName || "",
          duration: med.duration || "5 Days",
          endDate:
            med.endDate ||
            new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)
              .toISOString()
              .substring(0, 10),
          frequency: med.frequency || "1-0-1",
          instruction: med.instruction || "Take after food with water.",
          medicationId: med.medicationId || null,
          modifiedBy: "ADMIN",
          modifiedDate: nowStr,
          route: med.route || "Oral",
          startDate:
            med.startDate || new Date().toISOString().substring(0, 10),
          status: 1,
          strength: med.strength || "500 mg",
        })
      ),

      followupDate: dischargeState.followupDate || `${nowStr.substring(0, 10)} 10:00:00`,
      followupDepartmentDesc:
        dischargeState.followupDepartmentDesc || "General Medicine",
      followupDepartmentId:
        dischargeState.followupDepartmentId || "DEPT001",
      followupDoctorId: dischargeState.followupDoctorId || "DOC002",
      followupDoctorName:
        dischargeState.followupDoctorName || "Dr. Arun Kumar",
      followupInstruction: dischargeState.followupInstruction || "",

      dischargePatientInstruction: (
        dischargeState.dischargePatientInstruction || []
      ).map((ins, idx) => ({
        createdBy: "ADMIN",
        createdDate: nowStr,
        dischSummaryId: dischargeState.dischSummaryId || null,
        instrCode: ins.instrCode || `INS00${idx + 1}`,
        instrName: ins.instrName || "",
        instructionId: ins.instructionId || idx + 1,
        modifiedBy: "ADMIN",
        modifiedDate: nowStr,
        status: 1,
      })),

      dischargeDietDtls: (dischargeState.dischargeDietDtls || []).map(
        (diet, idx) => ({
          createdBy: "ADMIN",
          createdDate: nowStr,
          dietCode: diet.dietCode || `DIET00${idx + 1}`,
          dietId: diet.dietId || idx + 1,
          dietName: diet.dietName || "",
          dischSummaryId: dischargeState.dischSummaryId || null,
          modifiedBy: "ADMIN",
          modifiedDate: nowStr,
          status: 1,
        })
      ),

      dischargeCarePlanDtls: (dischargeState.dischargeCarePlanDtls || []).map(
        (cp, idx) => ({
          carePlanCode: cp.carePlanCode || `CP00${idx + 1}`,
          carePlanId: cp.carePlanId || idx + 1,
          carePlanName: cp.carePlanName || "",
          createdBy: "ADMIN",
          createdDate: nowStr,
          dischSummaryId: dischargeState.dischSummaryId || null,
          modifiedBy: "ADMIN",
          modifiedDate: nowStr,
          status: 1,
        })
      ),

      dispositionDesc: dischargeState.dispositionDesc || "Discharged Home",
      dispositionId: dischargeState.dispositionId || "DISP001",
      doctorRemark: dischargeState.doctorRemark || "",
      additionalInstruction: dischargeState.additionalInstruction || "",
      authDocId: sessionDoctorId,
      authDocName: sessionDoctorName,
      accountId: dischargeState.accountId || "ACC001",
      clinicId: dischargeState.clinicId || "CLN001",
      createdBy: dischargeState.createdBy || "ADMIN",
      createdDate: dischargeState.createdDate || nowStr,
      digitalSignature: dischargeState.digitalSignature || "base 64 image",
      digitalSignatureType: dischargeState.digitalSignatureType || "jpg",
      dischSummaryId: dischargeState.dischSummaryId || null,
      encounterId: dischargeState.encounterId || "ENC001",
      modifiedBy: "ADMIN",
      modifiedDate: nowStr,
      pageNo: dischargeState.pageNo || 0,
      pageSize: dischargeState.pageSize || 10,
      patientCounseled: dischargeState.patientCounseled || "Y",
      patientId: dischargeState.patientId || "PAT001",
      status: dischargeState.status || 1,
      summaryNumber: dischargeState.summaryNumber || "DS20260001",
    };

    console.log("%c====== FINAL DISCHARGE SUMMARY PAYLOAD ======", "color: #0d9488; font-weight: bold; font-size: 14px;");
    console.log("PAYLOAD OBJECT:", payload);
    console.log("STRINGIFIED JSON:", JSON.stringify(payload, null, 2));

    // Dispatch to redux action which also logs and handles save/update
    dispatch(
      saveDischargeSummaryAction(payload, (res) => {
        if (onSaveSuccess) onSaveSuccess(res);
      })
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
      <h3 className="text-sm font-bold text-teal-700 mb-4 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-teal-600"></span>
        14. Doctor Authentication & Actions
      </h3>

      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div className="flex flex-wrap items-start gap-6 sm:gap-8">
          <div className="w-full sm:w-[200px]">
            <label className="block text-xs font-semibold mb-2 text-gray-700">
              Doctor Name
            </label>
            <div className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 bg-gray-50 text-gray-700 font-medium">
              {sessionDoctorName}
            </div>
          </div>

          <div className="w-full sm:w-[130px]">
            <label className="block text-xs font-semibold mb-2 text-gray-700">
              Patient Counseled <span className="text-red-500">*</span>
            </label>
            <select
              value={patientCounseled}
              onChange={(e) =>
                dispatch(setDischargeField("patientCounseled", e.target.value))
              }
              className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 outline-none focus:border-teal-500 bg-white transition"
            >
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
          </div>

          <div className="flex flex-col">
            <label className="block text-xs font-semibold mb-2 text-gray-700">
              Digital Signature
            </label>
            <div className="flex items-center gap-4 border border-dashed border-gray-300 px-4 py-2 rounded-md bg-gray-50/50">
              <div
                className="text-gray-600 text-lg italic"
                style={{ fontFamily: "cursive" }}
              >
                {sessionDoctorName.replace("Dr. ", "") || "Doctor Signature"}
              </div>
             
            </div>
          </div>
        </div>

        <div className="flex flex-wrap lg:justify-end items-end gap-4 sm:gap-5">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[11px] text-gray-500 font-medium max-w-[150px] leading-tight">
              Note: Please review before saving the discharge summary.
            </span>
            <button
              type="button"
              className="flex items-center text-white gap-2 px-4 py-2 bg-white border border-gray-200 rounded-md text-sm font-semibold hover:bg-gray-50 transition text-gray-700 shadow-sm"
            >
              <FiEye className="text-teal-600" /> Preview
            </button>
          
            <button
              type="button"
              disabled={isSaving}
              onClick={handleSaveDischarge}
              className="flex items-center text-white gap-2 px-6 py-2 bg-teal-700 text-white rounded-md text-sm font-semibold hover:bg-teal-800 shadow-sm transition disabled:opacity-50 cursor-pointer"
            >
              <FiCheckCircle />
              {isSaving
                ? "Saving..."
                : dischSummaryId
                ? "Update Discharge Summary"
                : "Save Discharge Summary"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
